export const STORAGE_KEY = 'melodia.dungeon-market.v2';
export const ENERGY_REGEN_MS = 3 * 60 * 1000;
export const RETURN_REPORT_MIN_MS = 10 * 60 * 1000;
export const MAX_MARKET_LEVEL = 50;
export const BASE_MAX_ENERGY = 100;
export const MAX_INVENTORY_QUANTITY = 999999;
const MAX_LEDGER_NUMBER = Number.MAX_SAFE_INTEGER;
const STATE_TABS = new Set(['overview', 'dungeons', 'market', 'stall', 'collection', 'leaderboard', 'guild', 'friends']);
export const RENOVATIONS = Object.freeze({
  counter: {
    id: 'counter',
    name: 'Appraiser’s Counter',
    icon: '◇',
    description: 'Polished brass and better scales encourage stronger customer offers.',
    effect: 'Customer offers',
    costs: [2200, 5200, 9800, 16000],
  },
  atlas: {
    id: 'atlas',
    name: 'Wayfinder Atlas',
    icon: '⌁',
    description: 'Mapped shortcuts bring every future expedition home sooner.',
    effect: 'Expedition time',
    costs: [2800, 6500, 11500, 18500],
  },
  cistern: {
    id: 'cistern',
    name: 'Aether Cistern',
    icon: 'ϟ',
    description: 'A larger reservoir permanently expands your maximum energy.',
    effect: 'Energy capacity',
    costs: [2400, 5600, 10000, 16500],
  },
});
export const WEEKLY_CONTRACT = Object.freeze({
  target: 3,
  gems: 8,
  marketScore: 2400,
  xp: 120,
});
export const DAILY_COMMISSIONS = Object.freeze([
  {
    id: 'counter-service',
    metric: 'customers',
    target: 2,
    icon: '⌂',
    title: 'Keep the counter warm',
    description: 'Complete two customer sales at your stall.',
    reward: { gold: 350, gems: 0, marketScore: 180, xp: 25 },
  },
  {
    id: 'gate-report',
    metric: 'expeditions',
    target: 1,
    icon: '♧',
    title: 'Bring back a gate report',
    description: 'Complete one expedition at any depth.',
    reward: { gold: 0, gems: 2, marketScore: 300, xp: 40 },
  },
  {
    id: 'moving-stock',
    metric: 'trades',
    target: 2,
    icon: '◈',
    title: 'Keep stock moving',
    description: 'Complete two buy or sell transactions in the market.',
    reward: { gold: 500, gems: 0, marketScore: 220, xp: 30 },
  },
]);
export const DAILY_COMMISSION_BONUS = Object.freeze({
  gems: 3,
  marketScore: 750,
  xp: 60,
});
export const DAILY_REWARDS = Object.freeze([
  { day: 1, gold: 250, gems: 0 },
  { day: 2, gold: 400, gems: 1 },
  { day: 3, gold: 600, gems: 0 },
  { day: 4, gold: 850, gems: 2 },
  { day: 5, gold: 1100, gems: 0 },
  { day: 6, gold: 1500, gems: 3 },
  { day: 7, gold: 2400, gems: 5 },
]);
export const MARKET_PATH_REWARDS = Object.freeze([
  { level: 5, type: 'gold', amount: 600 },
  { level: 10, type: 'gems', amount: 5 },
  { level: 15, type: 'item', itemId: 'grotto-key', quantity: 1 },
  { level: 20, type: 'gold', amount: 1600 },
  { level: 25, type: 'gems', amount: 10 },
  { level: 30, type: 'item', itemId: 'glass-oracle', quantity: 1 },
  { level: 35, type: 'gold', amount: 4000 },
  { level: 40, type: 'gems', amount: 20 },
  { level: 45, type: 'item', itemId: 'ember-idol', quantity: 1 },
  { level: 50, type: 'item', itemId: 'sunken-crown', quantity: 1 },
]);
export const ACHIEVEMENTS = Object.freeze([
  { id: 'keeper-oath', title: 'Keeper’s Oath', description: 'Open stall seventeen and finish the keeper’s introduction.', icon: '◆', tone: 'gold', target: 1, unit: 'stall opened', reward: { gold: 300, gems: 0 } },
  { id: 'seventh-bell', title: 'At the Seventh Bell', description: 'Reach a seven-day Keeper’s Calendar streak.', icon: '▦', tone: 'cyan', target: 7, unit: 'day streak', reward: { gold: 0, gems: 3 } },
  { id: 'familiar-faces', title: 'Familiar Faces', description: 'Serve twenty-five customers across the life of your stall.', icon: '⌂', tone: 'orange', target: 25, unit: 'customers', reward: { gold: 1000, gems: 0 } },
  { id: 'field-codex', title: 'Field Codex', description: 'Permanently catalogue six different dungeon-market finds.', icon: '▣', tone: 'violet', target: 6, unit: 'discoveries', reward: { gold: 0, gems: 5 } },
  { id: 'heavy-purse', title: 'A Heavy Purse', description: 'Hold twenty-five thousand gold at one time.', icon: '◇', tone: 'gold', target: 25000, unit: 'gold', reward: { gold: 1200, gems: 0 } },
  { id: 'seasoned-crew', title: 'Seasoned Crew', description: 'Raise any adventurer to level three.', icon: '♧', tone: 'green', target: 3, unit: 'adventurer level', reward: { gold: 0, gems: 5 } },
  { id: 'lantern-and-oak', title: 'Lantern & Oak', description: 'Renovate stall seventeen to level ten.', icon: '♜', tone: 'orange', target: 10, unit: 'stall level', reward: { gold: 2000, gems: 0 } },
  { id: 'pathfinder', title: 'Gilded Pathfinder', description: 'Reach Market level twenty-five.', icon: '♛', tone: 'gold', target: 25, unit: 'Market level', reward: { gold: 0, gems: 8 } },
  { id: 'crown-witness', title: 'Witness to a Crown', description: 'Record the legendary Sunken Crown in your permanent codex.', icon: '♕', tone: 'violet', target: 1, unit: 'legend recorded', reward: { gold: 0, gems: 10 } },
  { id: 'masterwork', title: 'Masterwork Keeper', description: 'Raise any stall renovation to its fifth and final rank.', icon: '✦', tone: 'cyan', target: 5, unit: 'renovation rank', reward: { gold: 0, gems: 15 } },
]);

export const ITEMS = {
  'sunken-crown': {
    id: 'sunken-crown',
    name: 'Sunken Crown',
    category: 'Relic',
    rarity: 'Legendary',
    icon: '♕',
    tone: 'gold',
    value: 4200,
    lore: 'A drowned sovereign still whispers through its salt-worn points.',
  },
  'moonlit-signet': {
    id: 'moonlit-signet',
    name: 'Moonlit Signet',
    category: 'Relic',
    rarity: 'Rare',
    icon: '☾',
    tone: 'cyan',
    value: 920,
    lore: 'Cold silver that gleams whenever a hidden door is near.',
  },
  'ember-idol': {
    id: 'ember-idol',
    name: 'Ember Idol',
    category: 'Relic',
    rarity: 'Epic',
    icon: '♨',
    tone: 'red',
    value: 2450,
    lore: 'Warm to the touch, even after a century beneath the vault.',
  },
  'glass-oracle': {
    id: 'glass-oracle',
    name: 'Glass Oracle',
    category: 'Relic',
    rarity: 'Epic',
    icon: '◇',
    tone: 'violet',
    value: 2800,
    lore: 'Its answer changes depending on who is brave enough to ask.',
  },
  'grotto-key': {
    id: 'grotto-key',
    name: 'Grotto Key',
    category: 'Curio',
    rarity: 'Uncommon',
    icon: '⚿',
    tone: 'green',
    value: 480,
    lore: 'Moss has grown through the bow, but its teeth remain sharp.',
  },
  mooncap: {
    id: 'mooncap',
    name: 'Mooncap',
    category: 'Ingredient',
    rarity: 'Common',
    icon: '✦',
    tone: 'violet',
    value: 70,
    lore: 'A cave mushroom prized by alchemists and insomniacs.',
  },
  'obsidian-shard': {
    id: 'obsidian-shard',
    name: 'Obsidian Shard',
    category: 'Material',
    rarity: 'Rare',
    icon: '◆',
    tone: 'blue',
    value: 250,
    lore: 'It holds an edge no bazaar smith has managed to dull.',
  },
  'root-amber': {
    id: 'root-amber',
    name: 'Root Amber',
    category: 'Material',
    rarity: 'Uncommon',
    icon: '●',
    tone: 'gold',
    value: 180,
    lore: 'Ancient sap with a tiny green flame suspended inside.',
  },
};

export const ADVENTURERS = {
  brann: {
    id: 'brann',
    name: 'Brann Copperhand',
    role: 'Vanguard',
    icon: '⚒',
    tone: 'orange',
    power: 5,
    luck: 1,
    trait: 'Bulwark',
    perk: 'Softens the penalty when a run goes wrong.',
  },
  lyra: {
    id: 'lyra',
    name: 'Lyra Glassstep',
    role: 'Scout',
    icon: '⌁',
    tone: 'cyan',
    power: 3,
    luck: 5,
    trait: 'Keen Eye',
    perk: 'Raises the chance of finding rarer loot.',
  },
  vesper: {
    id: 'vesper',
    name: 'Vesper Quill',
    role: 'Mystic',
    icon: '✦',
    tone: 'violet',
    power: 4,
    luck: 4,
    trait: 'Appraiser',
    perk: 'Adds 8% to gold recovered on successful runs.',
  },
  pip: {
    id: 'pip',
    name: 'Pip Mosswhistle',
    role: 'Tinker',
    icon: '⚙',
    tone: 'green',
    power: 4,
    luck: 2,
    trait: 'Packrat',
    perk: 'Returns with one extra material or ingredient.',
  },
};

export const CUSTOMERS = {
  elspeth: {
    id: 'elspeth',
    name: 'Lady Elspeth',
    title: 'Collector of Unquiet Things',
    icon: 'E',
    tone: 'violet',
    preference: 'Relic',
    greeting: 'I was told your cabinet contains something with a past.',
  },
  rowan: {
    id: 'rowan',
    name: 'Rowan Bell',
    title: 'Guild Provisioner',
    icon: 'R',
    tone: 'green',
    preference: 'Ingredient',
    greeting: 'The expedition kitchens are bare again. What can you spare?',
  },
  cinder: {
    id: 'cinder',
    name: 'Cinder Voss',
    title: 'Emberdeep Smith',
    icon: 'C',
    tone: 'red',
    preference: 'Material',
    greeting: 'Good ore speaks before the hammer falls. Let me hear yours.',
  },
  nib: {
    id: 'nib',
    name: 'Nib',
    title: 'Licensed Curiosity Inspector',
    icon: 'N',
    tone: 'cyan',
    preference: 'Curio',
    greeting: 'Official business. Probably. Show me the interesting shelf.',
  },
};

export const DUNGEONS = [
  {
    id: 'mossroot',
    name: 'Mossroot Grotto',
    level: 3,
    rewardMin: 1200,
    rewardMax: 2000,
    risk: 'Low',
    icon: '⌁',
    tone: 'green',
    cost: 10,
    powerTarget: 8,
    durationMs: 8 * 60 * 1000,
    quote: 'The roots keep what the city forgets.',
    loot: ['mooncap', 'root-amber', 'grotto-key', 'moonlit-signet'],
  },
  {
    id: 'catacombs',
    name: 'The Glass Catacombs',
    level: 7,
    rewardMin: 3800,
    rewardMax: 6500,
    risk: 'Medium',
    icon: '◇',
    tone: 'cyan',
    cost: 18,
    powerTarget: 13,
    durationMs: 14 * 60 * 1000,
    quote: 'Where the walls remember every name.',
    loot: ['obsidian-shard', 'moonlit-signet', 'glass-oracle', 'sunken-crown'],
  },
  {
    id: 'emberdeep',
    name: 'Emberdeep Vault',
    level: 12,
    rewardMin: 10000,
    rewardMax: 18000,
    risk: 'High',
    icon: '♨',
    tone: 'red',
    cost: 28,
    powerTarget: 18,
    durationMs: 22 * 60 * 1000,
    quote: 'Every locked door is warm on the other side.',
    loot: ['obsidian-shard', 'ember-idol', 'glass-oracle', 'sunken-crown'],
  },
];

export const MARKET_LISTINGS = [
  { id: 'listing-crown', itemId: 'sunken-crown', quantity: 1, price: 4200, seller: 'Mossbeard' },
  { id: 'listing-mooncaps', itemId: 'mooncap', quantity: 12, price: 860, seller: 'Luma' },
  { id: 'listing-obsidian', itemId: 'obsidian-shard', quantity: 5, price: 1250, seller: 'KettleKid' },
  { id: 'listing-key', itemId: 'grotto-key', quantity: 1, price: 510, seller: 'The Last Lantern' },
  { id: 'listing-idol', itemId: 'ember-idol', quantity: 1, price: 2680, seller: 'Sable & Sons' },
];

function dateKey(now) {
  const date = new Date(now);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function boundedInteger(value, minimum = 0, maximum = MAX_LEDGER_NUMBER) {
  const number = Number(value);
  if (!Number.isFinite(number)) return minimum;
  return Math.max(minimum, Math.min(maximum, Math.floor(number)));
}

function reliquaryRarity(itemId) {
  if (itemId === 'sunken-crown') return 'Legendary';
  if (itemId === 'ember-idol' || itemId === 'glass-oracle') return 'Epic';
  return 'Rare';
}

function dayDifference(fromKey, toKey) {
  if (!fromKey) return Number.POSITIVE_INFINITY;
  const from = new Date(`${fromKey}T00:00:00`);
  const to = new Date(`${toKey}T00:00:00`);
  return Math.round((to - from) / 86400000);
}

function weekKey(now) {
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  const daysSinceMonday = (monday.getDay() + 6) % 7;
  monday.setDate(monday.getDate() - daysSinceMonday);
  return dateKey(monday.getTime());
}

function createDailyCommissions(now) {
  return {
    dayKey: dateKey(now),
    progress: Object.fromEntries(DAILY_COMMISSIONS.map(commission => [commission.metric, 0])),
    claimed: [],
    bonusClaimed: false,
  };
}

export function createInitialState(now = Date.now()) {
  const initial = {
    version: 2,
    gold: 1500,
    gems: 20,
    energy: BASE_MAX_ENERGY,
    maxEnergy: BASE_MAX_ENERGY,
    energyUpdatedAt: now,
    lastActiveAt: now,
    streak: 0,
    loginCycleDay: 1,
    lastClaimDate: null,
    marketDay: dateKey(now),
    dailyCommissions: createDailyCommissions(now),
    weekly: {
      weekKey: weekKey(now),
      expeditions: 0,
      claimed: false,
      startingMarketScore: 0,
    },
    onboardingComplete: false,
    activeTab: 'overview',
    expedition: null,
    adventurers: Object.keys(ADVENTURERS).map(id => ({ id, xp: 0, level: 1 })),
    selectedParty: ['brann', 'lyra', 'vesper'],
    inventory: [
      { itemId: 'moonlit-signet', quantity: 1 },
      { itemId: 'mooncap', quantity: 8 },
      { itemId: 'obsidian-shard', quantity: 3 },
      { itemId: 'root-amber', quantity: 2 },
    ],
    discoveries: ['moonlit-signet', 'mooncap', 'obsidian-shard', 'root-amber'],
    purchasedListings: [],
    marketScore: 0,
    todayHaul: 0,
    level: 1,
    xp: 0,
    pity: 0,
    summonHistory: [],
    summonStats: {
      total: 0,
      Legendary: 0,
      Epic: 0,
      Rare: 0,
    },
    marketPath: {
      claimed: [],
    },
    achievements: {
      unlocked: [],
      claimed: [],
    },
    stall: {
      level: 1,
      renovations: {
        counter: 1,
        atlas: 1,
        cistern: 1,
      },
      reputation: 0,
      customersServed: 0,
      dailyRevenue: 0,
      offer: null,
    },
    journal: ['The keeper handed you the keys to stall seventeen.'],
  };
  initial.stall.offer = generateCustomerOffer(initial, () => 0.24);
  return initial;
}

export function addInventory(inventory, itemId, quantity = 1) {
  const next = inventory.map(entry => ({ ...entry }));
  const existing = next.find(entry => entry.itemId === itemId);
  const change = Number.isFinite(Number(quantity)) ? Math.trunc(Number(quantity)) : 0;
  if (existing) {
    existing.quantity = Math.max(
      0,
      Math.min(MAX_INVENTORY_QUANTITY, existing.quantity + change),
    );
  } else if (change > 0) {
    next.push({ itemId, quantity: Math.min(MAX_INVENTORY_QUANTITY, change) });
  }
  return next.filter(entry => entry.quantity > 0);
}

export function recordDiscoveries(state, itemIds) {
  const additions = Array.isArray(itemIds) ? itemIds : [itemIds];
  const known = new Set(
    (Array.isArray(state.discoveries) ? state.discoveries : [])
      .filter(itemId => ITEMS[itemId]),
  );
  additions.filter(itemId => ITEMS[itemId]).forEach(itemId => known.add(itemId));
  const discoveries = Object.keys(ITEMS).filter(itemId => known.has(itemId));
  if (
    discoveries.length === state.discoveries?.length
    && discoveries.every((itemId, index) => itemId === state.discoveries[index])
  ) return state;
  return { ...state, discoveries };
}

function renovationLevels(state) {
  return Object.fromEntries(Object.keys(RENOVATIONS).map(id => [
    id,
    Math.max(1, Math.min(5, Math.floor(Number(state.stall?.renovations?.[id]) || 1))),
  ]));
}

export function stallLevel(state) {
  return 1 + Object.values(renovationLevels(state))
    .reduce((total, level) => total + Math.max(0, level - 1), 0);
}

export function renovationQuote(state, renovationId) {
  const renovation = RENOVATIONS[renovationId];
  if (!renovation) return null;
  const level = renovationLevels(state)[renovationId];
  const maxed = level >= 5;
  return {
    ...renovation,
    level,
    nextLevel: maxed ? null : level + 1,
    maxed,
    cost: maxed ? null : renovation.costs[level - 1],
    currentBonus: renovationBonusLabel(renovationId, level),
    nextBonus: maxed ? null : renovationBonusLabel(renovationId, level + 1),
  };
}

function renovationBonusLabel(renovationId, level) {
  const upgrades = Math.max(0, level - 1);
  if (renovationId === 'counter') return `+${upgrades * 5}% value`;
  if (renovationId === 'atlas') return `-${upgrades * 5}% time`;
  return `${BASE_MAX_ENERGY + upgrades * 5} max`;
}

export function customerOfferMultiplier(state) {
  return 1 + (renovationLevels(state).counter - 1) * 0.05;
}

export function expeditionDuration(state, dungeonOrId) {
  const dungeon = typeof dungeonOrId === 'string'
    ? DUNGEONS.find(entry => entry.id === dungeonOrId)
    : dungeonOrId;
  if (!dungeon) return 0;
  const speedBonus = (renovationLevels(state).atlas - 1) * 0.05;
  return Math.round(dungeon.durationMs * (1 - speedBonus));
}

export function renovationEnergyCap(state) {
  return BASE_MAX_ENERGY + (renovationLevels(state).cistern - 1) * 5;
}

export function upgradeRenovation(state, renovationId) {
  const quote = renovationQuote(state, renovationId);
  if (!quote) return { state, ok: false, message: 'That renovation is not in the keeper’s plans.' };
  if (quote.maxed) return { state, ok: false, message: `${quote.name} is already masterwork quality.` };
  if (state.gold < quote.cost) return { state, ok: false, message: `You need ${(quote.cost - state.gold).toLocaleString()} more gold for ${quote.name}.` };

  const previousOfferMultiplier = customerOfferMultiplier(state);
  const renovations = { ...renovationLevels(state), [renovationId]: quote.nextLevel };
  let next = {
    ...state,
    gold: state.gold - quote.cost,
    marketScore: state.marketScore + Math.round(quote.cost * 0.12),
    stall: { ...state.stall, renovations },
    journal: [
      `Renovation complete: ${quote.name} reached level ${quote.nextLevel}.`,
      ...state.journal,
    ].slice(0, 12),
  };
  next.stall.level = stallLevel(next);
  const previousCap = Math.max(BASE_MAX_ENERGY, Number(state.maxEnergy) || BASE_MAX_ENERGY);
  next.maxEnergy = renovationEnergyCap(next);
  next.energy = Math.min(next.maxEnergy, state.energy + Math.max(0, next.maxEnergy - previousCap));
  if (renovationId === 'counter' && next.stall.offer) {
    next.stall.offer = {
      ...next.stall.offer,
      offer: Math.max(
        next.stall.offer.offer + 1,
        Math.round(next.stall.offer.offer * customerOfferMultiplier(next) / previousOfferMultiplier),
      ),
    };
  }
  next = awardMarketXp(next, 25 + quote.nextLevel * 10);
  return {
    state: next,
    ok: true,
    message: `${quote.name} upgraded to level ${quote.nextLevel}: ${quote.nextBonus}.`,
  };
}

export function xpForMarketLevel(level) {
  const safeLevel = Math.max(1, Math.min(MAX_MARKET_LEVEL, Math.floor(Number(level) || 1)));
  return 8 * (safeLevel - 1) ** 2;
}

export function marketLevelForXp(xp) {
  const safeXp = Math.max(0, Math.floor(Number(xp) || 0));
  return Math.min(MAX_MARKET_LEVEL, Math.floor(Math.sqrt(safeXp / 8)) + 1);
}

export function marketLevelProgress(state) {
  const xp = Math.max(0, Math.floor(Number(state.xp) || 0));
  const level = marketLevelForXp(xp);
  if (level >= MAX_MARKET_LEVEL) {
    return { level, xp, current: xpForMarketLevel(level), next: null, earned: 0, needed: 0, remaining: 0, percent: 100 };
  }
  const current = xpForMarketLevel(level);
  const next = xpForMarketLevel(level + 1);
  const earned = Math.max(0, xp - current);
  const needed = next - current;
  return {
    level,
    xp,
    current,
    next,
    earned,
    needed,
    remaining: Math.max(0, next - xp),
    percent: Math.min(100, Math.round(earned / needed * 100)),
  };
}

export function awardMarketXp(state, amount) {
  const gained = Math.max(0, Math.floor(Number(amount) || 0));
  if (!gained) return state;
  const previousLevel = marketLevelForXp(state.xp);
  const xp = Math.max(0, Math.floor(Number(state.xp) || 0)) + gained;
  const level = marketLevelForXp(xp);
  return {
    ...state,
    xp,
    level,
    journal: level > previousLevel
      ? [`Market level ${level} reached. New buyers have noticed stall seventeen.`, ...(state.journal || [])].slice(0, 12)
      : state.journal,
  };
}

export function claimMarketPathReward(state, rewardLevel) {
  const level = Math.floor(Number(rewardLevel) || 0);
  const reward = MARKET_PATH_REWARDS.find(entry => entry.level === level);
  const claimed = Array.isArray(state.marketPath?.claimed) ? state.marketPath.claimed : [];
  if (!reward) return { state, ok: false, message: 'That Market Path seal does not exist.' };
  if (state.level < level) return { state, ok: false, message: `Reach Market level ${level} to break this seal.` };
  if (claimed.includes(level)) return { state, ok: false, message: `The level ${level} Market Path reward was already claimed.` };

  let next = {
    ...state,
    marketPath: {
      ...state.marketPath,
      claimed: [...claimed, level].sort((left, right) => left - right),
    },
  };
  let rewardName;
  if (reward.type === 'gold') {
    next.gold += reward.amount;
    rewardName = `${reward.amount.toLocaleString()} gold`;
  } else if (reward.type === 'gems') {
    next.gems += reward.amount;
    rewardName = `${reward.amount} gems`;
  } else {
    const item = ITEMS[reward.itemId];
    next.inventory = addInventory(next.inventory, reward.itemId, reward.quantity);
    next = recordDiscoveries(next, reward.itemId);
    rewardName = `${reward.quantity}× ${item.name}`;
  }
  next.journal = [
    `Market Path level ${level}: claimed ${rewardName}.`,
    ...(Array.isArray(next.journal) ? next.journal : []),
  ].slice(0, 12);
  return {
    state: next,
    ok: true,
    reward,
    message: `${rewardName} claimed from Market Path level ${level}.`,
  };
}

export function claimAvailableMarketPathRewards(state) {
  const available = MARKET_PATH_REWARDS.filter(reward => (
    reward.level <= state.level
    && !state.marketPath?.claimed?.includes(reward.level)
  ));
  if (!available.length) return { state, ok: false, message: 'No Market Path rewards are ready yet.' };
  let next = state;
  for (const reward of available) next = claimMarketPathReward(next, reward.level).state;
  return {
    state: next,
    ok: true,
    rewards: available,
    message: `${available.length} Market Path reward${available.length === 1 ? '' : 's'} claimed.`,
  };
}

function achievementCurrent(state, achievementId) {
  if (achievementId === 'keeper-oath') return Number(Boolean(state.onboardingComplete));
  if (achievementId === 'seventh-bell') return Math.max(0, Number(state.streak) || 0);
  if (achievementId === 'familiar-faces') return Math.max(0, Number(state.stall?.customersServed) || 0);
  if (achievementId === 'field-codex') return new Set(state.discoveries || []).size;
  if (achievementId === 'heavy-purse') return Math.max(0, Number(state.gold) || 0);
  if (achievementId === 'seasoned-crew') return Math.max(1, ...(state.adventurers || []).map(member => Number(member.level) || 1));
  if (achievementId === 'lantern-and-oak') return Math.max(1, Number(state.stall?.level) || 1);
  if (achievementId === 'pathfinder') return Math.max(1, Number(state.level) || 1);
  if (achievementId === 'crown-witness') return Number(Boolean(state.discoveries?.includes('sunken-crown')));
  if (achievementId === 'masterwork') return Math.max(1, ...Object.values(state.stall?.renovations || {}).map(level => Number(level) || 1));
  return 0;
}

export function achievementProgress(state, achievementOrId) {
  const achievement = typeof achievementOrId === 'string'
    ? ACHIEVEMENTS.find(entry => entry.id === achievementOrId)
    : achievementOrId;
  if (!achievement) return null;
  const current = Math.min(achievement.target, Math.max(0, Math.floor(achievementCurrent(state, achievement.id))));
  const unlocked = Boolean(state.achievements?.unlocked?.includes(achievement.id))
    || current >= achievement.target;
  return {
    current,
    target: achievement.target,
    percent: Math.min(100, Math.round(current / achievement.target * 100)),
    unlocked,
    claimed: Boolean(state.achievements?.claimed?.includes(achievement.id)),
  };
}

export function refreshAchievements(state) {
  const storedUnlocked = Array.isArray(state.achievements?.unlocked) ? state.achievements.unlocked : [];
  const previous = storedUnlocked.filter(id => ACHIEVEMENTS.some(achievement => achievement.id === id));
  const unlocked = ACHIEVEMENTS
    .filter(achievement => previous.includes(achievement.id) || achievementProgress(state, achievement).unlocked)
    .map(achievement => achievement.id);
  const claimed = Array.isArray(state.achievements?.claimed)
    ? state.achievements.claimed.filter(id => unlocked.includes(id))
    : [];
  const additions = unlocked.filter(id => !previous.includes(id));
  if (
    !additions.length
    && state.achievements
    && unlocked.length === storedUnlocked.length
    && unlocked.every((id, index) => id === storedUnlocked[index])
    && claimed.length === state.achievements.claimed?.length
  ) return state;
  return {
    ...state,
    achievements: { unlocked, claimed },
    journal: additions.length
      ? [
          ...additions.map(id => `Keeper’s Chronicle: “${ACHIEVEMENTS.find(entry => entry.id === id).title}” unlocked.`),
          ...(Array.isArray(state.journal) ? state.journal : []),
        ].slice(0, 12)
      : state.journal,
  };
}

export function claimAchievement(state, achievementId) {
  state = refreshAchievements(state);
  const achievement = ACHIEVEMENTS.find(entry => entry.id === achievementId);
  if (!achievement) return { state, ok: false, message: 'That Chronicle seal does not exist.' };
  if (!state.achievements.unlocked.includes(achievementId)) return { state, ok: false, message: `“${achievement.title}” is still locked.` };
  if (state.achievements.claimed.includes(achievementId)) return { state, ok: false, message: `“${achievement.title}” was already claimed.` };
  const next = {
    ...state,
    gold: state.gold + achievement.reward.gold,
    gems: state.gems + achievement.reward.gems,
    achievements: {
      ...state.achievements,
      claimed: [...state.achievements.claimed, achievementId],
    },
    journal: [`Keeper’s Chronicle: claimed “${achievement.title}”.`, ...state.journal].slice(0, 12),
  };
  const reward = [
    achievement.reward.gold ? `${achievement.reward.gold.toLocaleString()} gold` : '',
    achievement.reward.gems ? `${achievement.reward.gems} gems` : '',
  ].filter(Boolean).join(' and ');
  return { state: next, ok: true, achievement, message: `${achievement.title}: ${reward} claimed.` };
}

export function claimAvailableAchievements(state) {
  state = refreshAchievements(state);
  const ready = ACHIEVEMENTS.filter(achievement => (
    state.achievements.unlocked.includes(achievement.id)
    && !state.achievements.claimed.includes(achievement.id)
  ));
  if (!ready.length) return { state, ok: false, message: 'No Chronicle rewards are ready yet.' };
  let next = state;
  for (const achievement of ready) next = claimAchievement(next, achievement.id).state;
  return {
    state: next,
    ok: true,
    achievements: ready,
    message: `${ready.length} Chronicle reward${ready.length === 1 ? '' : 's'} claimed.`,
  };
}

export function regenerateEnergy(state, now = Date.now()) {
  const updatedAt = Number(state.energyUpdatedAt) || now;
  const elapsed = Math.max(0, now - updatedAt);
  const gained = Math.floor(elapsed / ENERGY_REGEN_MS);
  if (gained < 1 || state.energy >= state.maxEnergy) return state;
  return {
    ...state,
    energy: Math.min(state.maxEnergy, state.energy + gained),
    energyUpdatedAt: updatedAt + gained * ENERGY_REGEN_MS,
  };
}

export function energyRecoveryForecast(state, now = Date.now()) {
  const energy = Math.max(0, Math.floor(Number(state?.energy) || 0));
  const maximum = Math.max(energy, Math.floor(Number(state?.maxEnergy) || BASE_MAX_ENERGY));
  if (energy >= maximum) return { nextIn: 0, fullIn: 0, missing: 0 };
  const updatedAt = Number(state?.energyUpdatedAt);
  const elapsed = Math.max(0, now - (Number.isFinite(updatedAt) ? updatedAt : now));
  const remainder = elapsed % ENERGY_REGEN_MS;
  const nextIn = remainder === 0 ? ENERGY_REGEN_MS : ENERGY_REGEN_MS - remainder;
  const missing = maximum - energy;
  return {
    nextIn,
    fullIn: nextIn + (missing - 1) * ENERGY_REGEN_MS,
    missing,
  };
}

export function summarizeAbsence(saved, current, now = Date.now()) {
  if (!saved || saved.version !== 2 || !saved.onboardingComplete || !current) return null;
  const lastActiveAt = Number(saved.lastActiveAt);
  const awayMs = now - lastActiveAt;
  if (!Number.isFinite(lastActiveAt) || awayMs < RETURN_REPORT_MIN_MS) return null;

  const energyBefore = Math.max(0, Math.floor(Number(saved.energy) || 0));
  const energyGained = Math.max(0, current.energy - energyBefore);
  const expeditionReady = Boolean(
    saved.expedition
    && Number(saved.expedition.endsAt) <= now
    && current.expedition,
  );
  const marketRolled = typeof saved.marketDay === 'string'
    && saved.marketDay !== current.marketDay;
  const weeklyRolled = typeof saved.weekly?.weekKey === 'string'
    && saved.weekly.weekKey !== current.weekly?.weekKey;

  if (!energyGained && !expeditionReady && !marketRolled && !weeklyRolled) return null;
  return {
    awayMs,
    energyBefore,
    energyGained,
    energyAfter: current.energy,
    maxEnergy: current.maxEnergy,
    expeditionReady,
    dungeonId: expeditionReady ? saved.expedition.dungeonId : null,
    marketRolled,
    weeklyRolled,
  };
}

export function rollDailyState(state, now = Date.now()) {
  const today = dateKey(now);
  if (state.marketDay === today && state.dailyCommissions?.dayKey === today) return state;
  return {
    ...state,
    marketDay: today,
    dailyCommissions: createDailyCommissions(now),
    todayHaul: 0,
    purchasedListings: [],
    stall: {
      ...state.stall,
      dailyRevenue: 0,
    },
    journal: [
      'Market bell: a new trading day began and the keeper board was refreshed.',
      ...(Array.isArray(state.journal) ? state.journal : []),
    ].slice(0, 12),
  };
}

export function rollWeeklyState(state, now = Date.now()) {
  const currentWeek = weekKey(now);
  if (state.weekly?.weekKey === currentWeek) return state;
  return {
    ...state,
    weekly: {
      weekKey: currentWeek,
      expeditions: 0,
      claimed: false,
      startingMarketScore: Math.max(0, Math.floor(Number(state.marketScore) || 0)),
    },
    journal: [
      'Guild bell: a new weekly contract was posted in Copper & Clover.',
      ...(Array.isArray(state.journal) ? state.journal : []),
    ].slice(0, 12),
  };
}

export function hydrateState(saved, now = Date.now()) {
  const base = createInitialState(now);
  const returningPlayer = Boolean(saved && saved.version === 2);
  let next = returningPlayer ? { ...base, ...saved } : base;
  next.lastActiveAt = Number.isFinite(Number(saved?.lastActiveAt))
    ? Math.min(now, Math.max(0, Number(saved.lastActiveAt)))
    : now;
  next.onboardingComplete = typeof saved?.onboardingComplete === 'boolean'
    ? saved.onboardingComplete
    : returningPlayer;
  next.gold = boundedInteger(next.gold);
  next.gems = boundedInteger(next.gems, 0, 1000000000);
  next.marketScore = boundedInteger(next.marketScore);
  next.todayHaul = boundedInteger(next.todayHaul);
  next.pity = boundedInteger(next.pity, 0, 9);
  next.activeTab = STATE_TABS.has(next.activeTab) ? next.activeTab : 'overview';
  next.energyUpdatedAt = Math.min(now, boundedInteger(next.energyUpdatedAt, 0, now));
  next.streak = boundedInteger(next.streak);
  next.loginCycleDay = Math.max(
    1,
    Math.min(DAILY_REWARDS.length, Math.floor(Number(saved?.loginCycleDay) || Math.min(DAILY_REWARDS.length, next.streak || 1))),
  );
  next.lastClaimDate = typeof next.lastClaimDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(next.lastClaimDate)
    ? next.lastClaimDate
    : null;
  next.inventory = (Array.isArray(next.inventory) ? next.inventory : base.inventory)
    .reduce((inventory, entry) => {
      const quantity = boundedInteger(entry?.quantity, 0, MAX_INVENTORY_QUANTITY);
      return ITEMS[entry?.itemId] && quantity > 0
        ? addInventory(inventory, entry.itemId, quantity)
        : inventory;
    }, [])
    .map(entry => ({ ...entry, quantity: Math.min(MAX_INVENTORY_QUANTITY, entry.quantity) }));
  next = recordDiscoveries({
    ...next,
    discoveries: Array.isArray(saved?.discoveries) ? saved.discoveries : [],
  }, next.inventory.map(entry => entry.itemId));
  next.purchasedListings = Array.isArray(next.purchasedListings)
    ? [...new Set(next.purchasedListings.filter(id => MARKET_LISTINGS.some(listing => listing.id === id)))]
    : [];
  next.journal = Array.isArray(next.journal)
    ? next.journal
      .filter(entry => typeof entry === 'string')
      .map(entry => entry.trim().slice(0, 280))
      .filter(Boolean)
      .slice(0, 12)
    : [];
  next.xp = boundedInteger(next.xp);
  next.level = marketLevelForXp(next.xp);
  next.summonHistory = Array.isArray(next.summonHistory)
    ? next.summonHistory
      .filter(entry => ITEMS[entry?.itemId])
      .map(entry => ({
        itemId: entry.itemId,
        rarity: reliquaryRarity(entry.itemId),
        guaranteed: Boolean(entry.guaranteed),
        newDiscovery: Boolean(entry.newDiscovery),
        drawnAt: Number.isFinite(Number(entry.drawnAt)) ? Number(entry.drawnAt) : now,
      }))
      .slice(0, 30)
    : [];
  const recordedSummons = next.summonHistory.reduce((counts, entry) => ({
    ...counts,
    [entry.rarity]: (counts[entry.rarity] || 0) + 1,
  }), { Legendary: 0, Epic: 0, Rare: 0 });
  const savedSummonStats = next.summonStats && typeof next.summonStats === 'object'
    ? next.summonStats
    : recordedSummons;
  next.summonStats = {
    Legendary: Math.max(recordedSummons.Legendary, boundedInteger(savedSummonStats.Legendary)),
    Epic: Math.max(recordedSummons.Epic, boundedInteger(savedSummonStats.Epic)),
    Rare: Math.max(recordedSummons.Rare, boundedInteger(savedSummonStats.Rare)),
    total: 0,
  };
  next.summonStats.total = Math.max(
    next.summonStats.Legendary + next.summonStats.Epic + next.summonStats.Rare,
    boundedInteger(savedSummonStats.total),
  );
  next.marketPath = {
    claimed: Array.isArray(next.marketPath?.claimed)
      ? [...new Set(next.marketPath.claimed
        .map(level => Math.floor(Number(level) || 0))
        .filter(level => MARKET_PATH_REWARDS.some(reward => reward.level === level)))]
        .sort((left, right) => left - right)
      : [],
  };
  next.achievements = {
    unlocked: Array.isArray(next.achievements?.unlocked)
      ? [...new Set(next.achievements.unlocked.filter(id => ACHIEVEMENTS.some(achievement => achievement.id === id)))]
      : [],
    claimed: Array.isArray(next.achievements?.claimed)
      ? [...new Set(next.achievements.claimed.filter(id => ACHIEVEMENTS.some(achievement => achievement.id === id)))]
      : [],
  };
  next.stall = {
    ...base.stall,
    ...(next.stall && typeof next.stall === 'object' && !Array.isArray(next.stall) ? next.stall : {}),
  };
  next.stall.reputation = boundedInteger(next.stall.reputation);
  next.stall.customersServed = boundedInteger(next.stall.customersServed);
  next.stall.dailyRevenue = boundedInteger(next.stall.dailyRevenue);
  next.stall.renovations = renovationLevels(next);
  next.stall.level = stallLevel(next);
  next.maxEnergy = renovationEnergyCap(next);
  next.energy = boundedInteger(next.energy, 0, next.maxEnergy);
  next = regenerateEnergy(next, now);
  const savedDaily = next.dailyCommissions && typeof next.dailyCommissions === 'object' && !Array.isArray(next.dailyCommissions)
    ? next.dailyCommissions
    : {};
  next.dailyCommissions = {
    ...base.dailyCommissions,
    ...savedDaily,
    progress: Object.fromEntries(DAILY_COMMISSIONS.map(commission => [
      commission.metric,
      boundedInteger(savedDaily.progress?.[commission.metric], 0, commission.target),
    ])),
    claimed: Array.isArray(savedDaily.claimed)
      ? [...new Set(savedDaily.claimed.filter(id => DAILY_COMMISSIONS.some(commission => commission.id === id)))]
      : [],
    bonusClaimed: Boolean(savedDaily.bonusClaimed),
  };
  if (returningPlayer && typeof saved.marketDay !== 'string') next.marketDay = null;
  next = rollDailyState(next, now);
  next.weekly = {
    ...base.weekly,
    ...(next.weekly && typeof next.weekly === 'object' && !Array.isArray(next.weekly) ? next.weekly : {}),
    expeditions: boundedInteger(next.weekly?.expeditions),
    claimed: Boolean(next.weekly?.claimed),
    startingMarketScore: Number.isFinite(Number(next.weekly?.startingMarketScore))
      ? boundedInteger(next.weekly.startingMarketScore)
      : next.marketScore,
  };
  next = rollWeeklyState(next, now);
  const savedOffer = next.stall.offer;
  const offerQuantity = boundedInteger(savedOffer?.quantity, 0, 999);
  const availableForOffer = next.inventory.find(entry => entry.itemId === savedOffer?.itemId)?.quantity || 0;
  const validOffer = Boolean(
    savedOffer
    && CUSTOMERS[savedOffer.customerId]
    && ITEMS[savedOffer.itemId]
    && offerQuantity >= 1
    && offerQuantity <= availableForOffer
    && boundedInteger(savedOffer.offer) >= 1
  );
  if (!validOffer) {
    next.stall.offer = generateCustomerOffer(next, () => 0.31);
  } else {
    next.stall.offer = {
      id: typeof savedOffer.id === 'string' ? savedOffer.id.slice(0, 100) : `${savedOffer.customerId}-${savedOffer.itemId}-${offerQuantity}`,
      customerId: savedOffer.customerId,
      itemId: savedOffer.itemId,
      quantity: offerQuantity,
      offer: boundedInteger(savedOffer.offer, 1),
      patience: boundedInteger(savedOffer.patience, 0, 2),
      originalOffer: savedOffer.originalOffer == null ? null : boundedInteger(savedOffer.originalOffer, 1),
    };
  }
  next.adventurers = Array.isArray(next.adventurers)
    ? next.adventurers
      .filter((entry, index, entries) => ADVENTURERS[entry?.id] && entries.findIndex(candidate => candidate?.id === entry.id) === index)
      .map(entry => ({
      id: entry.id,
      xp: boundedInteger(entry.xp),
      level: boundedInteger(entry.level, 1, 10),
    }))
    : base.adventurers;
  for (const member of base.adventurers) {
    if (!next.adventurers.some(entry => entry.id === member.id)) next.adventurers.push(member);
  }
  next.selectedParty = Array.isArray(next.selectedParty)
    ? next.selectedParty.filter((id, index, ids) => ADVENTURERS[id] && ids.indexOf(id) === index).slice(0, 3)
    : base.selectedParty;
  if (next.selectedParty.length < 2) next.selectedParty = base.selectedParty;
  const savedExpedition = next.expedition;
  const dungeon = DUNGEONS.find(entry => entry.id === savedExpedition?.dungeonId);
  const startedAt = Number(savedExpedition?.startedAt);
  const endsAt = Number(savedExpedition?.endsAt);
  if (
    dungeon
    && Number.isFinite(startedAt)
    && Number.isFinite(endsAt)
    && startedAt >= 0
    && endsAt >= startedAt
    && startedAt <= now + 5 * 60 * 1000
    && endsAt - startedAt <= 24 * 60 * 60 * 1000
  ) {
    const partyIds = Array.isArray(savedExpedition.partyIds)
      ? savedExpedition.partyIds
        .filter((id, index, ids) => ADVENTURERS[id] && ids.indexOf(id) === index)
        .slice(0, 3)
      : [...next.selectedParty];
    const safePartyIds = partyIds.length >= 2 ? partyIds : [...next.selectedParty];
    next.expedition = {
      dungeonId: dungeon.id,
      startedAt,
      endsAt,
      partyIds: safePartyIds,
      party: partyStats(next, safePartyIds),
      notifyOnComplete: Boolean(savedExpedition.notifyOnComplete),
      notifiedAt: Number.isFinite(Number(savedExpedition.notifiedAt))
        ? Math.max(0, Math.min(now, Number(savedExpedition.notifiedAt)))
        : null,
    };
  } else {
    next.expedition = null;
  }
  return refreshAchievements(next);
}

export function weeklyStanding(state) {
  const lifetime = Math.max(0, Math.floor(Number(state?.marketScore) || 0));
  const starting = Math.max(0, Math.floor(Number(state?.weekly?.startingMarketScore) || 0));
  return Math.max(0, lifetime - starting);
}

export function generateCustomerOffer(state, random = Math.random) {
  const inventory = state.inventory.filter(entry => entry.quantity > 0 && ITEMS[entry.itemId]);
  if (!inventory.length) return null;
  const customerIds = Object.keys(CUSTOMERS);
  const customerId = customerIds[Math.min(customerIds.length - 1, Math.floor(random() * customerIds.length))];
  const customer = CUSTOMERS[customerId];
  const preferred = inventory.filter(entry => ITEMS[entry.itemId].category === customer.preference);
  const pool = preferred.length && random() < 0.7 ? preferred : inventory;
  const owned = pool[Math.min(pool.length - 1, Math.floor(random() * pool.length))];
  const item = ITEMS[owned.itemId];
  const stackable = item.category === 'Material' || item.category === 'Ingredient';
  const maxQuantity = stackable ? Math.min(3, owned.quantity) : 1;
  const quantity = 1 + Math.min(maxQuantity - 1, Math.floor(random() * maxQuantity));
  const preferenceBonus = item.category === customer.preference ? 0.13 : 0;
  const multiplier = (0.78 + random() * 0.24 + preferenceBonus) * customerOfferMultiplier(state);
  return {
    id: `${customerId}-${item.id}-${quantity}`,
    customerId,
    itemId: item.id,
    quantity,
    offer: Math.max(1, Math.round(item.value * quantity * multiplier)),
    patience: 2,
    originalOffer: null,
  };
}

export function ensureCustomerOffer(state, random = Math.random) {
  const offer = state.stall?.offer;
  const available = offer
    ? state.inventory.find(entry => entry.itemId === offer.itemId)?.quantity || 0
    : 0;
  if (offer && ITEMS[offer.itemId] && available >= offer.quantity) return state;
  return {
    ...state,
    stall: {
      ...(state.stall || createInitialState().stall),
      offer: generateCustomerOffer(state, random),
    },
  };
}

export function acceptCustomerOffer(state, random = Math.random, now = Date.now()) {
  state = rollDailyState(state, now);
  const offer = state.stall?.offer;
  if (!offer) return { state, ok: false, message: 'There is no customer at the counter.' };
  const owned = state.inventory.find(entry => entry.itemId === offer.itemId);
  if (!owned || owned.quantity < offer.quantity) {
    return { state, ok: false, message: 'The requested stock is no longer available.' };
  }
  const customer = CUSTOMERS[offer.customerId];
  const item = ITEMS[offer.itemId];
  const reputationGain = Math.max(1, Math.round(offer.offer / Math.max(1, item.value * offer.quantity) * 3));
  let next = {
    ...state,
    gold: state.gold + offer.offer,
    todayHaul: state.todayHaul + offer.offer,
    marketScore: state.marketScore + offer.offer + reputationGain * 25,
    inventory: addInventory(state.inventory, offer.itemId, -offer.quantity),
    stall: {
      ...state.stall,
      reputation: state.stall.reputation + reputationGain,
      customersServed: state.stall.customersServed + 1,
      dailyRevenue: state.stall.dailyRevenue + offer.offer,
      offer: null,
    },
    journal: [`Stall sale: ${offer.quantity}× ${item.name} to ${customer.name} for ${offer.offer.toLocaleString()} gold.`, ...state.journal].slice(0, 12),
  };
  next = recordDailyProgress(awardMarketXp(next, 20 + reputationGain * 5), 'customers', 1, now);
  next.stall.offer = generateCustomerOffer(next, random);
  return {
    ok: true,
    message: `${customer.name} paid ${offer.offer.toLocaleString()} gold for ${offer.quantity}× ${item.name}.`,
    state: next,
  };
}

export function haggleCustomerOffer(state, random = Math.random) {
  const offer = state.stall?.offer;
  if (!offer) return { state, ok: false, message: 'There is no customer to negotiate with.' };
  const customer = CUSTOMERS[offer.customerId];
  const item = ITEMS[offer.itemId];
  const successChance = Math.max(0.28, 0.7 - (2 - offer.patience) * 0.22);
  const successful = random() < successChance;
  if (successful) {
    const increase = 1.08 + random() * 0.08;
    const raised = Math.max(offer.offer + 1, Math.round(offer.offer * increase));
    return {
      ok: true,
      message: `${customer.name} raises the offer to ${raised.toLocaleString()} gold.`,
      state: {
        ...state,
        stall: {
          ...state.stall,
          offer: {
            ...offer,
            offer: raised,
            originalOffer: offer.originalOffer || offer.offer,
            patience: Math.max(0, offer.patience - 1),
          },
        },
      },
    };
  }
  const patience = offer.patience - 1;
  if (patience <= 0) {
    const next = {
      ...state,
      stall: { ...state.stall, reputation: Math.max(0, state.stall.reputation - 1), offer: null },
      journal: [`${customer.name} left after negotiations over ${item.name} broke down.`, ...state.journal].slice(0, 12),
    };
    next.stall.offer = generateCustomerOffer(next, random);
    return { ok: false, message: `${customer.name} walks away from the counter.`, state: next };
  }
  return {
    ok: false,
    message: `${customer.name} refuses, but remains at the counter.`,
    state: { ...state, stall: { ...state.stall, offer: { ...offer, patience } } },
  };
}

export function declineCustomerOffer(state, random = Math.random) {
  const offer = state.stall?.offer;
  if (!offer) return { state, ok: false, message: 'There is no offer to decline.' };
  const customer = CUSTOMERS[offer.customerId];
  const next = {
    ...state,
    stall: { ...state.stall, offer: null },
    journal: [`Declined ${customer.name}’s offer at the stall.`, ...state.journal].slice(0, 12),
  };
  next.stall.offer = generateCustomerOffer(next, random);
  return { ok: true, message: `${customer.name} leaves politely. A new customer approaches.`, state: next };
}

export function partyStats(state, partyIds = state.selectedParty) {
  const members = [...new Set(partyIds)].slice(0, 3)
    .map(id => {
      const rosterEntry = state.adventurers?.find(entry => entry.id === id);
      return rosterEntry && ADVENTURERS[id] ? { ...ADVENTURERS[id], ...rosterEntry } : null;
    })
    .filter(Boolean);
  const roles = new Set(members.map(member => member.role));
  const balanced = members.length === 3 && roles.size === 3;
  const power = members.reduce((total, member) => total + member.power + Math.floor((member.level - 1) / 2), 0) + (balanced ? 2 : 0);
  const luck = members.reduce((total, member) => total + member.luck, 0) + (balanced ? 2 : 0);
  return {
    members,
    power,
    luck,
    synergy: balanced ? 'Balanced Crew' : roles.size < members.length ? 'Role overlap' : 'Mixed talents',
  };
}

export function togglePartyMember(state, adventurerId) {
  if (!ADVENTURERS[adventurerId]) return { state, ok: false, message: 'That adventurer is unavailable.' };
  const selected = [...state.selectedParty];
  const index = selected.indexOf(adventurerId);
  if (index >= 0) {
    if (selected.length <= 2) return { state, ok: false, message: 'An expedition needs at least two adventurers.' };
    selected.splice(index, 1);
    return { ok: true, state: { ...state, selectedParty: selected }, message: `${ADVENTURERS[adventurerId].name} is resting this run.` };
  }
  if (selected.length >= 3) return { state, ok: false, message: 'Your party already has three adventurers.' };
  selected.push(adventurerId);
  return { ok: true, state: { ...state, selectedParty: selected }, message: `${ADVENTURERS[adventurerId].name} joined the party.` };
}

export function expeditionForecast(state, dungeonId, partyIds = state.selectedParty) {
  const dungeon = DUNGEONS.find(entry => entry.id === dungeonId);
  const party = partyStats(state, partyIds);
  if (!dungeon) return { party, successChance: 0, rating: 'Unknown' };
  const successChance = Math.max(0.2, Math.min(0.95, 0.58 + (party.power - dungeon.powerTarget) * 0.045));
  const rating = successChance >= 0.82 ? 'Favored' : successChance >= 0.62 ? 'Steady' : successChance >= 0.42 ? 'Risky' : 'Dire';
  return { party, successChance, rating };
}

export function recommendedDungeon(state) {
  const marketLevel = Math.max(1, Math.floor(Number(state?.level) || 1));
  return [...DUNGEONS]
    .reverse()
    .find(dungeon => dungeon.level <= marketLevel)
    || DUNGEONS[0];
}

export function canClaimDaily(state, now = Date.now()) {
  return dailyLoginStatus(state, now).canClaim;
}

export function dailyLoginStatus(state, now = Date.now()) {
  const today = dateKey(now);
  const validLastClaim = typeof state?.lastClaimDate === 'string'
    && /^\d{4}-\d{2}-\d{2}$/.test(state.lastClaimDate);
  const gap = validLastClaim ? dayDifference(state.lastClaimDate, today) : Number.POSITIVE_INFINITY;
  const cycleDay = Math.max(
    1,
    Math.min(DAILY_REWARDS.length, Math.floor(Number(state?.loginCycleDay) || Math.min(DAILY_REWARDS.length, Number(state?.streak) || 1))),
  );
  const claimed = gap === 0;
  const clockSkew = gap < 0;
  const consecutive = gap === 1;
  const rewardDay = claimed
    ? cycleDay
    : consecutive
      ? cycleDay % DAILY_REWARDS.length + 1
      : 1;
  return {
    today,
    gap,
    claimed,
    clockSkew,
    canClaim: !claimed && !clockSkew,
    cycleDay,
    rewardDay,
    reward: DAILY_REWARDS[rewardDay - 1],
    streakWillReset: Number.isFinite(gap) && gap > 1,
  };
}

export function claimDaily(state, now = Date.now()) {
  const status = dailyLoginStatus(state, now);
  if (!status.canClaim) {
    return {
      state,
      ok: false,
      message: status.clockSkew
        ? 'The keeper’s calendar is waiting for the recorded date to catch up.'
        : 'Today’s reward is already claimed.',
    };
  }
  const streak = status.gap === 1 ? Math.max(0, Number(state.streak) || 0) + 1 : 1;
  const reward = status.reward;
  return {
    ok: true,
    message: `Calendar Day ${status.rewardDay} claimed: ${reward.gold.toLocaleString()} gold${reward.gems ? ` and ${reward.gems} ${reward.gems === 1 ? 'gem' : 'gems'}` : ''}.`,
    state: awardMarketXp({
      ...state,
      streak,
      loginCycleDay: status.rewardDay,
      lastClaimDate: status.today,
      gold: state.gold + reward.gold,
      gems: state.gems + reward.gems,
      journal: [`Keeper’s calendar: cycle day ${status.rewardDay} claimed at a ${streak}-day streak.`, ...state.journal].slice(0, 12),
    }, 20 + status.rewardDay * 5),
  };
}

export function recordDailyProgress(state, metric, amount = 1, now = Date.now()) {
  const current = rollDailyState(state, now);
  const commissions = DAILY_COMMISSIONS.filter(commission => commission.metric === metric);
  if (!commissions.length) return current;
  const maximum = Math.max(...commissions.map(commission => commission.target));
  const gained = Math.max(0, Math.floor(Number(amount) || 0));
  if (!gained) return current;
  const previous = Math.max(0, Math.floor(Number(current.dailyCommissions.progress[metric]) || 0));
  const progress = Math.min(maximum, previous + gained);
  if (progress === previous) return current;
  return {
    ...current,
    dailyCommissions: {
      ...current.dailyCommissions,
      progress: { ...current.dailyCommissions.progress, [metric]: progress },
    },
  };
}

export function claimDailyCommission(state, commissionId, now = Date.now()) {
  const current = rollDailyState(state, now);
  const commission = DAILY_COMMISSIONS.find(entry => entry.id === commissionId);
  if (!commission) return { state: current, ok: false, message: 'That commission is not on today’s board.' };
  if (current.dailyCommissions.claimed.includes(commission.id)) {
    return { state: current, ok: false, message: `${commission.title} is already claimed.` };
  }
  const progress = current.dailyCommissions.progress[commission.metric] || 0;
  if (progress < commission.target) {
    return {
      state: current,
      ok: false,
      message: `${commission.target - progress} more needed for ${commission.title.toLowerCase()}.`,
    };
  }
  const reward = commission.reward;
  const rewarded = awardMarketXp({
    ...current,
    gold: current.gold + reward.gold,
    gems: current.gems + reward.gems,
    marketScore: current.marketScore + reward.marketScore,
    dailyCommissions: {
      ...current.dailyCommissions,
      claimed: [...current.dailyCommissions.claimed, commission.id],
    },
    journal: [
      `Daily commission: ${commission.title} completed.`,
      ...current.journal,
    ].slice(0, 12),
  }, reward.xp);
  return {
    state: rewarded,
    ok: true,
    message: `${commission.title} complete: ${commissionRewardLabel(reward)}.`,
  };
}

export function claimDailyCommissionBonus(state, now = Date.now()) {
  const current = rollDailyState(state, now);
  if (current.dailyCommissions.bonusClaimed) {
    return { state: current, ok: false, message: 'Today’s sealed ledger reward is already claimed.' };
  }
  const allClaimed = DAILY_COMMISSIONS.every(commission => current.dailyCommissions.claimed.includes(commission.id));
  if (!allClaimed) {
    return { state: current, ok: false, message: 'Claim every daily commission before sealing the ledger.' };
  }
  const rewarded = awardMarketXp({
    ...current,
    gems: current.gems + DAILY_COMMISSION_BONUS.gems,
    marketScore: current.marketScore + DAILY_COMMISSION_BONUS.marketScore,
    dailyCommissions: { ...current.dailyCommissions, bonusClaimed: true },
    journal: [
      `Daily ledger sealed: ${DAILY_COMMISSION_BONUS.gems} gems and ${DAILY_COMMISSION_BONUS.marketScore.toLocaleString()} standing.`,
      ...current.journal,
    ].slice(0, 12),
  }, DAILY_COMMISSION_BONUS.xp);
  return {
    state: rewarded,
    ok: true,
    message: `Ledger sealed: ${DAILY_COMMISSION_BONUS.gems} gems and ${DAILY_COMMISSION_BONUS.marketScore.toLocaleString()} standing.`,
  };
}

function commissionRewardLabel(reward) {
  return [
    reward.gold ? `${reward.gold.toLocaleString()} gold` : '',
    reward.gems ? `${reward.gems} gems` : '',
    reward.marketScore ? `${reward.marketScore.toLocaleString()} standing` : '',
    reward.xp ? `${reward.xp} XP` : '',
  ].filter(Boolean).join(' · ');
}

export function claimWeeklyContract(state, now = Date.now()) {
  const current = rollWeeklyState(state, now);
  if (current.weekly.claimed) {
    return { state: current, ok: false, message: 'This week’s guild contract is already claimed.' };
  }
  if (current.weekly.expeditions < WEEKLY_CONTRACT.target) {
    const remaining = WEEKLY_CONTRACT.target - current.weekly.expeditions;
    return { state: current, ok: false, message: `Complete ${remaining} more expedition${remaining === 1 ? '' : 's'} for the guild.` };
  }
  const rewarded = awardMarketXp({
    ...current,
    gems: current.gems + WEEKLY_CONTRACT.gems,
    marketScore: current.marketScore + WEEKLY_CONTRACT.marketScore,
    weekly: { ...current.weekly, claimed: true },
    journal: [
      `Guild contract claimed: ${WEEKLY_CONTRACT.gems} gems and ${WEEKLY_CONTRACT.marketScore.toLocaleString()} standing.`,
      ...current.journal,
    ].slice(0, 12),
  }, WEEKLY_CONTRACT.xp);
  return {
    state: rewarded,
    ok: true,
    message: `Guild contract complete: ${WEEKLY_CONTRACT.gems} gems and ${WEEKLY_CONTRACT.marketScore.toLocaleString()} standing.`,
  };
}

export function startExpedition(state, dungeonId, now = Date.now(), partyIds = state.selectedParty) {
  const dungeon = DUNGEONS.find(entry => entry.id === dungeonId);
  if (!dungeon) return { state, ok: false, message: 'That dungeon is not on today’s map.' };
  if (state.expedition) return { state, ok: false, message: 'Your party is already in the depths.' };
  const energized = regenerateEnergy(state, now);
  if (energized.energy < dungeon.cost) return { state: energized, ok: false, message: 'Your party needs more energy.' };
  const party = partyStats(energized, partyIds);
  if (party.members.length < 2 || party.members.length > 3) {
    return { state: energized, ok: false, message: 'Choose two or three adventurers before departing.' };
  }
  const startedFromFull = energized.energy >= energized.maxEnergy;
  return {
    ok: true,
    message: `Party dispatched to ${dungeon.name}.`,
    state: {
      ...energized,
      energy: energized.energy - dungeon.cost,
      // Spending below capacity must not erase a partially earned recharge
      // interval. A full reservoir starts a fresh interval when first spent.
      energyUpdatedAt: startedFromFull ? now : energized.energyUpdatedAt,
      selectedParty: party.members.map(member => member.id),
      expedition: {
        dungeonId,
        startedAt: now,
        endsAt: now + expeditionDuration(energized, dungeon),
        partyIds: party.members.map(member => member.id),
        party: { power: party.power, luck: party.luck, synergy: party.synergy },
        notifyOnComplete: false,
        notifiedAt: null,
      },
      journal: [`Expedition departed for ${dungeon.name}.`, ...energized.journal].slice(0, 12),
    },
  };
}

export function setExpeditionAlert(state, enabled) {
  if (!state.expedition) {
    return { state, ok: false, message: 'There is no active expedition to watch.' };
  }
  const armed = Boolean(enabled);
  return {
    ok: true,
    state: {
      ...state,
      expedition: {
        ...state.expedition,
        notifyOnComplete: armed,
        notifiedAt: armed ? state.expedition.notifiedAt || null : null,
      },
    },
    message: armed
      ? 'Alert armed. Keep Dungeon Market open and this tab will ring when the expedition returns.'
      : 'The expedition alert was turned off.',
  };
}

export function markExpeditionAlerted(state, now = Date.now()) {
  if (
    !state.expedition
    || !state.expedition.notifyOnComplete
    || state.expedition.notifiedAt
    || now < state.expedition.endsAt
  ) {
    return { state, ok: false };
  }
  return {
    ok: true,
    state: {
      ...state,
      expedition: {
        ...state.expedition,
        notifiedAt: now,
      },
    },
  };
}

export function completeExpedition(state, now = Date.now(), random = Math.random) {
  state = rollDailyState(rollWeeklyState(state, now), now);
  if (!state.expedition) return { state, ok: false, message: 'There is no expedition to collect.' };
  if (now < state.expedition.endsAt) return { state, ok: false, message: 'Your party is still searching the depths.' };
  const dungeon = DUNGEONS.find(entry => entry.id === state.expedition.dungeonId);
  const forecast = expeditionForecast(state, dungeon.id, state.expedition.partyIds || state.selectedParty);
  const { party, successChance } = forecast;
  const success = random() < successChance;
  const hasBulwark = party.members.some(member => member.id === 'brann');
  const hasAppraiser = party.members.some(member => member.id === 'vesper');
  const hasPackrat = party.members.some(member => member.id === 'pip');
  const baseGold = dungeon.rewardMin + (dungeon.rewardMax - dungeon.rewardMin) * random();
  const goldMultiplier = success ? (hasAppraiser ? 1.08 : 1) : (hasBulwark ? 0.42 : 0.28);
  const gold = Math.round(baseGold * goldMultiplier);
  const roll = random() + party.luck * 0.001;
  const lootIndex = roll > 0.985 ? 3 : roll > 0.88 ? 2 : roll > 0.42 ? 1 : 0;
  const itemId = dungeon.loot[lootIndex];
  const isStackable = ITEMS[itemId].category !== 'Relic' && ITEMS[itemId].category !== 'Curio';
  const quantity = isStackable ? 2 + Math.floor(random() * 4) + (hasPackrat ? 1 : 0) : 1;
  const item = ITEMS[itemId];
  const adventurerXp = success ? dungeon.level * 12 : dungeon.level * 5;
  const marketXp = success ? dungeon.level * 35 : dungeon.level * 12;
  const standing = success ? Math.round(gold * 0.18) + item.value : Math.round(gold * 0.08);
  const partyIds = party.members.map(member => member.id);
  const adventurers = state.adventurers.map(entry => {
    if (!party.members.some(member => member.id === entry.id)) return entry;
    const xp = entry.xp + adventurerXp;
    return { ...entry, xp, level: Math.min(10, 1 + Math.floor(xp / 100)) };
  });
  if (!success) {
    return {
      ok: true,
      reward: {
        success,
        successChance,
        gold,
        itemId: null,
        quantity: 0,
        dungeonId: dungeon.id,
        partyIds,
        adventurerXp,
        marketXp,
        standing,
      },
      message: `The party withdrew safely with ${gold.toLocaleString()} gold, but no marketable find.`,
      state: recordDailyProgress(awardMarketXp({
        ...state,
        gold: state.gold + gold,
        todayHaul: state.todayHaul + gold,
        marketScore: state.marketScore + standing,
        adventurers,
        weekly: { ...state.weekly, expeditions: state.weekly.expeditions + 1 },
        expedition: null,
        journal: [`${dungeon.name}: withdrew with ${gold.toLocaleString()} gold after a difficult run.`, ...state.journal].slice(0, 12),
      }, marketXp), 'expeditions', 1, now),
    };
  }
  return {
    ok: true,
    reward: {
      success,
      successChance,
      gold,
      itemId,
      quantity,
      dungeonId: dungeon.id,
      partyIds,
      adventurerXp,
      marketXp,
      standing,
    },
    message: `Returned with ${gold.toLocaleString()} gold and ${quantity}× ${item.name}.`,
    state: recordDailyProgress(recordDiscoveries(awardMarketXp({
      ...state,
      gold: state.gold + gold,
      todayHaul: state.todayHaul + gold,
      marketScore: state.marketScore + standing,
      adventurers,
      weekly: { ...state.weekly, expeditions: state.weekly.expeditions + 1 },
      inventory: addInventory(state.inventory, itemId, quantity),
      expedition: null,
      journal: [`${dungeon.name}: found ${quantity}× ${item.name} and ${gold.toLocaleString()} gold.`, ...state.journal].slice(0, 12),
    }, marketXp), itemId), 'expeditions', 1, now),
  };
}

export function buyListing(state, listingId, now = Date.now()) {
  state = rollDailyState(state, now);
  const listing = MARKET_LISTINGS.find(entry => entry.id === listingId);
  if (!listing) return { state, ok: false, message: 'That listing has left the market.' };
  if (state.purchasedListings.includes(listingId)) return { state, ok: false, message: 'That listing was already purchased.' };
  if (state.gold < listing.price) return { state, ok: false, message: 'Not enough gold for this listing.' };
  const item = ITEMS[listing.itemId];
  return {
    ok: true,
    message: `${listing.quantity}× ${item.name} added to your collection.`,
    state: recordDailyProgress(recordDiscoveries(awardMarketXp({
      ...state,
      gold: state.gold - listing.price,
      inventory: addInventory(state.inventory, listing.itemId, listing.quantity),
      purchasedListings: [...state.purchasedListings, listingId],
      marketScore: state.marketScore + Math.round(listing.price * 0.04),
      journal: [`Bought ${listing.quantity}× ${item.name} from ${listing.seller}.`, ...state.journal].slice(0, 12),
    }, Math.max(5, Math.round(listing.price / 250))), listing.itemId), 'trades', 1, now),
  };
}

export function sellItem(state, itemId, now = Date.now()) {
  state = rollDailyState(state, now);
  const owned = state.inventory.find(entry => entry.itemId === itemId);
  const item = ITEMS[itemId];
  if (!owned || !item) return { state, ok: false, message: 'That item is not in your collection.' };
  const proceeds = Math.max(1, Math.floor(item.value * 0.72));
  return {
    ok: true,
    message: `${item.name} sold for ${proceeds.toLocaleString()} gold.`,
    state: recordDailyProgress(awardMarketXp({
      ...state,
      gold: state.gold + proceeds,
      marketScore: state.marketScore + proceeds,
      inventory: addInventory(state.inventory, itemId, -1),
      journal: [`Sold ${item.name} for ${proceeds.toLocaleString()} gold.`, ...state.journal].slice(0, 12),
    }, Math.max(5, Math.round(proceeds / 100))), 'trades', 1, now),
  };
}

function drawOneRelic(state, random, now) {
  const forcedLegendary = state.pity >= 9;
  const roll = random();
  let itemId;
  let rarity;
  if (forcedLegendary || roll < 0.02) {
    itemId = 'sunken-crown';
    rarity = 'Legendary';
  } else if (roll < 0.14) {
    itemId = random() < 0.5 ? 'ember-idol' : 'glass-oracle';
    rarity = 'Epic';
  } else {
    itemId = random() < 0.5 ? 'moonlit-signet' : 'grotto-key';
    rarity = 'Rare';
  }
  return {
    itemId,
    rarity,
    guaranteed: forcedLegendary,
    pity: rarity === 'Legendary' ? 0 : state.pity + 1,
    history: { itemId, rarity, guaranteed: forcedLegendary, drawnAt: now },
  };
}

export function drawRelics(state, count = 1, random = Math.random, now = Date.now()) {
  if (![1, 10].includes(count)) return { state, ok: false, message: 'The Reliquary permits one or ten draws.' };
  const cost = count * 10;
  if (state.gems < cost) return { state, ok: false, message: `You need ${cost} gems for ${count === 1 ? 'a relic draw' : 'ten relic draws'}.` };
  let pity = state.pity;
  let inventory = state.inventory;
  const discovered = new Set(state.discoveries);
  const rewards = [];
  const history = [];
  for (let index = 0; index < count; index += 1) {
    const draw = drawOneRelic({ ...state, pity }, random, now + index);
    pity = draw.pity;
    const newDiscovery = !discovered.has(draw.itemId);
    discovered.add(draw.itemId);
    inventory = addInventory(inventory, draw.itemId, 1);
    const ownedAfter = inventory.find(entry => entry.itemId === draw.itemId)?.quantity || 1;
    rewards.push({
      itemId: draw.itemId,
      quantity: 1,
      rarity: draw.rarity,
      guaranteed: draw.guaranteed,
      newDiscovery,
      ownedAfter,
    });
    history.push({ ...draw.history, newDiscovery });
  }
  const legendaryCount = rewards.filter(reward => reward.rarity === 'Legendary').length;
  const epicCount = rewards.filter(reward => reward.rarity === 'Epic').length;
  const rareCount = rewards.filter(reward => reward.rarity === 'Rare').length;
  const previousStats = state.summonStats || { total: 0, Legendary: 0, Epic: 0, Rare: 0 };
  const summary = count === 1
    ? `${ITEMS[rewards[0].itemId].name} · ${rewards[0].rarity} joined your collection.`
    : `${count} relics revealed${legendaryCount ? ` · ${legendaryCount} Legendary` : epicCount ? ` · ${epicCount} Epic` : ''}.`;
  return {
    ok: true,
    reward: rewards[0],
    rewards,
    message: summary,
    state: recordDiscoveries(awardMarketXp({
      ...state,
      gems: state.gems - cost,
      pity,
      inventory,
      summonHistory: [...history.reverse(), ...state.summonHistory].slice(0, 30),
      summonStats: {
        total: Math.max(0, Number(previousStats.total) || 0) + count,
        Legendary: Math.max(0, Number(previousStats.Legendary) || 0) + legendaryCount,
        Epic: Math.max(0, Number(previousStats.Epic) || 0) + epicCount,
        Rare: Math.max(0, Number(previousStats.Rare) || 0) + rareCount,
      },
      journal: [`Reliquary: ${summary}`, ...state.journal].slice(0, 12),
    }, count * 6 + legendaryCount * 20 + epicCount * 8), rewards.map(reward => reward.itemId)),
  };
}

export function drawRelic(state, random = Math.random, now = Date.now()) {
  return drawRelics(state, 1, random, now);
}
